/**
 * FRONT-2669 — the one quiet line the chat shows for the tools that put a
 * document, sketch or deck beside the chat. Pure text: the component
 * (`quiet-line.tsx`) and the phone's message renderer both call this, so the
 * wording cannot drift and neither can ever print the tool's INPUT (a document
 * body) — the line is built only from a title, a count and fixed words.
 *
 * Shapes mirror the real producers (src/artifacts/tool.ts): a success carries
 * `saved: true` (documents) or `ok: true` (artifact_open, artifact_restore_previous)
 * plus `canvas: { open, kind, id, title, changes? }`; a refusal is a sentence
 * for the model with neither marker.
 */

export const QUIET_LINE_TOOLS = [
  'artifact_document_create',
  'artifact_open',
  'artifact_document_edit',
  'artifact_document_write',
  'artifact_restore_previous',
] as const;

export type QuietLineToolName = (typeof QUIET_LINE_TOOLS)[number];

export function isQuietLineTool(name: string): name is QuietLineToolName {
  return (QUIET_LINE_TOOLS as readonly string[]).includes(name);
}

export interface QuietLineTool {
  name: string;
  state?: string;
  input?: unknown;
  output?: unknown;
}

export interface QuietLine {
  text: string;
  /** The Canvas item this line is about (`document:<filename>` ...), or null when it cannot be told. */
  itemKey: string | null;
}

const MAX_TITLE = 80;
const FALLBACK = 'a document';

function rec(v: unknown): Record<string, unknown> | null {
  if (typeof v === 'string') {
    try {
      return rec(JSON.parse(v));
    } catch {
      return null;
    }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

/** A title is short by nature; clip anything longer so a body can never ride in through it. */
function quoted(title: string | null): string {
  if (!title) return FALLBACK;
  const t = title.length > MAX_TITLE ? `${title.slice(0, MAX_TITLE - 1)}…` : title;
  return `“${t}”`;
}

type Phase = 'running' | 'success' | 'refusal';

function phaseOf(name: QuietLineToolName, state: string | undefined, out: Record<string, unknown> | null, hasOutput: boolean): Phase {
  if (state === 'output-error') return 'refusal';
  if (!hasOutput) return 'running';
  if (!out) return 'refusal';
  const ok = name === 'artifact_restore_previous' || name === 'artifact_open' ? out.ok === true : out.saved === true;
  return ok ? 'success' : 'refusal';
}

function itemKeyOf(name: QuietLineToolName, input: Record<string, unknown> | null, out: Record<string, unknown> | null): string | null {
  if (name === 'artifact_restore_previous') {
    const id = str(input?.id);
    return id ? `${str(input?.kind) ?? 'document'}:${id}` : null;
  }
  const canvas = rec(out?.canvas);
  const kind = str(canvas?.kind) ?? (out?.saved === true ? 'document' : null);
  const id = str(canvas?.id) ?? str(out?.filename) ?? (name === 'artifact_open' ? str(input?.id) : null) ?? (name === 'artifact_document_edit' || name === 'artifact_document_write' ? str(input?.name) : null);
  const inKind = name === 'artifact_open' ? str(input?.kind) : null;
  const k = kind ?? inKind ?? (name === 'artifact_open' ? null : 'document');
  return k && id ? `${k}:${id}` : null;
}

function titleOf(name: QuietLineToolName, input: Record<string, unknown> | null, out: Record<string, unknown> | null, itemKey: string | null, resolveTitle?: (key: string) => string | null): string | null {
  const known = itemKey && resolveTitle ? resolveTitle(itemKey) : null;
  return (
    str(rec(out?.canvas)?.title) ??
    str(out?.title) ??
    known ??
    (name === 'artifact_document_create' ? str(input?.title) : null) ??
    (name === 'artifact_open' ? str(input?.id) : null) ??
    (name === 'artifact_document_edit' || name === 'artifact_document_write' ? str(input?.name) : null) ??
    null
  );
}

function changesOf(out: Record<string, unknown> | null): number | null {
  const n = out?.changes ?? rec(out?.canvas)?.changes;
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/**
 * The quiet line for one tool part, or null when the tool is not one of the
 * five (the caller renders its usual card). `resolveTitle` lets the caller
 * supply a title the Canvas already holds for an item key (a restore's output
 * carries none).
 */
export function quietLineOf(tool: QuietLineTool, resolveTitle?: (key: string) => string | null): QuietLine | null {
  if (!isQuietLineTool(tool.name)) return null;
  const name = tool.name;
  const input = rec(tool.input);
  const hasOutput = tool.output !== undefined && tool.output !== null;
  const out = rec(tool.output);
  const phase = phaseOf(name, tool.state, out, hasOutput);
  const itemKey = itemKeyOf(name, input, out);
  const t = quoted(titleOf(name, input, out, itemKey, resolveTitle));
  const editing = name === 'artifact_document_edit' || name === 'artifact_document_write';

  const opening = name === 'artifact_document_create' || name === 'artifact_open';
  if (phase === 'refusal') return { text: opening ? `Couldn't open ${t}` : `Couldn't update ${t}`, itemKey: null };
  if (phase === 'running') {
    if (name === 'artifact_restore_previous') return { text: `Restoring the earlier version of ${t}…`, itemKey };
    return { text: editing ? `Updating ${t} beside the chat…` : `Opening ${t} beside the chat…`, itemKey };
  }
  if (name === 'artifact_restore_previous') return { text: `Restored the earlier version of ${t}`, itemKey };
  if (name === 'artifact_document_write') return { text: `Updated ${t} beside the chat`, itemKey };
  if (name === 'artifact_document_edit') {
    const n = changesOf(out);
    const tail = n === null ? '' : ` · ${n} ${n === 1 ? 'change' : 'changes'}`;
    return { text: `Updated ${t} beside the chat${tail}`, itemKey };
  }
  return { text: `Opened ${t} beside the chat`, itemKey };
}
